// src/shared/changelog/components/WhatsNewModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { canReachPath } from "@shared/navigation/SideMenu/pathReachability";
import { useLaunchWalkthrough } from "@shared/tour/WalkthroughTourProvider";
import { getLatestReleaseGroup } from "../getChangelog";
import { formatReleaseDate, formatReleasePeriod } from "../format";
import type { ChangelogMonthGroup } from "../grouping";
import { entriesToMarkOnExit, splitWalkthrough } from "../autoOpenRules";
import { useSeenEntries } from "../useSeenEntries";
import WhatsNewMedia from "./WhatsNewMedia";

export interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fired once per opening, when the dialog first has an entry to render.
   *
   * Distinct from `isOpen` turning true: the release is fetched on open, so
   * between the two this renders nothing at all. Anything that must happen
   * because the reader was *actually shown* the announcement — spending the
   * release's one auto-open (§2.5) — belongs here, not at the open.
   */
  onShown?: () => void;
}

/**
 * A quiet outlined control for the prev / next arrows. Not `GenericButton`:
 * these are 24px square glyphs sitting beside a full-size primary action, and
 * every `GenericButton` size is taller than the row they share.
 */
const ARROW_CLASS = `
  flex h-6 w-6 items-center justify-center rounded
  border border-semantic-border-secondary
  text-semantic-text-secondary
  transition-colors motion-reduce:transition-none
  hover:bg-semantic-bg-secondary hover:text-semantic-text-primary
  disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent
  focus-visible:outline-none focus-visible:ring-2
  focus-visible:ring-semantic-border-brand-secondary
`;

/**
 * A cursor with rays — "I will point this out on the screen". Inlined the way
 * the footer arrows are, rather than added to `getIconByName`: that map holds
 * three generic icons shared app-wide, and this one means nothing outside this
 * button.
 */
const CursorRaysIcon: React.FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59"
    />
  </svg>
);

/**
 * "What's new" — a walkthrough of the newest release, one entry at a time
 * (CTX-224).
 *
 * The release is the newest **month** of the feed; see `getLatestReleaseGroup`
 * for why a month rather than a day. The tile above each entry is derived from
 * it rather than authored — see `WhatsNewMedia`. The header's pill carries that period and
 * the date on the right belongs to the entry currently shown, which stays
 * honest when a month spans several deploys.
 *
 * **The dialog owns what counts as read** (CTX-591). It used to be the caller,
 * which marked the whole release seen on the click that opened this — so closing
 * at `1 of 3` cleared an announcement two thirds of which was never rendered.
 * Now `Done` marks the walkthrough and `✕` marks only the entries that were
 * actually shown (§2.2), and the dialog opens on the first entry outside the
 * seen set rather than always on the first (§2.3).
 */
const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
  isOpen,
  onClose,
  onShown,
}) => {
  const axiosClient = useAxiosAuth0();
  const navigate = useNavigate();
  const launchWalkthrough = useLaunchWalkthrough();
  const { seenEntries, isLoaded: isSeenLoaded, markSeen } = useSeenEntries();

  // Reachability inputs (CTX-516). The rail asks the same questions of the
  // same store; the CTA must not answer them differently.
  const { TenantType, ProductList, ListPermiso } = useAppSelector(
    (state) => state.userProfile
  );
  const { tenantId, partnerId, tenantName, partnerName } = useAppSelector(
    (state) => state.selection
  );

  const [group, setGroup] = useState<ChangelogMonthGroup | null>(null);
  const [index, setIndex] = useState(0);
  /**
   * The entries this opening actually put in front of the reader — what a `✕`
   * marks. Accumulated as they are shown rather than derived from `index` at
   * close time, because the reader can page backwards: stopping on `2 of 3`
   * after reaching `3 of 3` has still shown all three.
   */
  const [shownKeys, setShownKeys] = useState<Set<string>>(new Set());
  /**
   * Whether this opening has already chosen its starting entry.
   *
   * The choice depends on the seen set, and marking entries changes that set
   * while the dialog is still open — so without this the reader would be moved
   * off the entry they are reading the moment anything is marked.
   */
  const hasPositioned = useRef(false);

  /**
   * Fetched on open rather than on mount: the card sits in every rail, and the
   * walkthrough is a dialog nobody has asked for until they click. In practice
   * the request is already resolved — `getChangelog` memoises one per session
   * and the card's own `useUnseenRelease` fired it — so this settles on the
   * next microtask and the dialog does not visibly wait.
   *
   * A failure leaves `group` null and the dialog renders nothing. The card only
   * exists when the feed resolved, so this is the narrow case of the feed
   * failing between the two reads, and an empty dialog would be worse than none.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    getLatestReleaseGroup(axiosClient)
      .then((latest) => {
        if (alive) setGroup(latest);
      })
      .catch(() => {
        /* no walkthrough */
      });
    return () => {
      alive = false;
    };
  }, [isOpen, axiosClient]);

  useEffect(() => {
    if (!isOpen) hasPositioned.current = false;
  }, [isOpen]);


  /**
   * Open on the first entry the reader has not seen (§2.3).
   *
   * This is what makes a second entry published into a month the reader already
   * walked through start on **that** entry instead of replaying `1 of N`. It
   * waits on `isSeenLoaded` because an empty set that has not answered yet is
   * indistinguishable from one that says nothing was read, and guessing wrong
   * here re-shows an entry and mis-marks it on a `✕`.
   *
   * A release with nothing left unseen — a manual open from the card — starts at
   * the top, which is the only sensible place to reread from.
   */
  useEffect(() => {
    if (!isOpen || hasPositioned.current || !isSeenLoaded) return;
    const { walked } = splitWalkthrough(group?.entries ?? []);
    if (walked.length === 0) return;

    const firstUnseen = walked.findIndex(
      (candidate) => !seenEntries.has(candidate.entryKey)
    );
    const start = firstUnseen === -1 ? 0 : firstUnseen;

    hasPositioned.current = true;
    setIndex(start);
    setShownKeys(new Set([walked[start].entryKey]));

    // Positioning happens exactly once per opening, and only once there is an
    // entry to land on — which makes it the moment the dialog genuinely shows
    // something, and the only honest place to tell the caller so.
    onShown?.();
  }, [isOpen, group, isSeenLoaded, seenEntries, onShown]);

  if (!isOpen || !group || group.entries.length === 0) return null;

  /**
   * The walkthrough is capped at `MAX_ENTRIES_PER_WALKTHROUGH` (§2.5). Anything
   * past it becomes one closing card pointing at the archive rather than more
   * cards to page through — the recorrido is an announcement, and `/changelog`
   * is the place that holds everything.
   */
  const { walked, overflowCount } = splitWalkthrough(group.entries);
  const hasOverflow = overflowCount > 0;
  const total = walked.length + (hasOverflow ? 1 : 0);
  // Clamped rather than trusted: a shorter release arriving while the dialog is
  // open would otherwise index past the end.
  const position = Math.min(index, total - 1);
  const isOverflowCard = hasOverflow && position === walked.length;
  /** `null` on the closing card, which stands for a count rather than a change. */
  const entry = isOverflowCard ? null : walked[position];
  const isLast = position === total - 1;

  /**
   * A single-entry release hides the dots, the arrows and the counter. "1 of 1"
   * beside two dead arrows advertises a walkthrough that does not exist.
   */
  const isPaginated = total > 1;

  /** One dot per card; the closing card has no entry to key it by. */
  const cardKeys = [
    ...walked.map((item) => item.entryKey),
    ...(hasOverflow ? ["__overflow__"] : []),
  ];

  /** The entries this opening actually rendered, in the release's own order. */
  const shownEntries = walked.filter((item) => shownKeys.has(item.entryKey));

  /** Moves to `nextIndex`, recording that its entry was put in front of the reader. */
  const showEntryAt = (nextIndex: number) => {
    setIndex(nextIndex);
    const nextEntry = walked[nextIndex];
    // The closing card is not an entry and marks nothing — reaching it says the
    // reader saw a count, not that they read the changes behind it.
    if (!nextEntry) return;
    const { entryKey } = nextEntry;
    setShownKeys((prev) =>
      prev.has(entryKey) ? prev : new Set(prev).add(entryKey)
    );
  };

  /**
   * Every way out that is not `Done` — `✕`, Esc, the backdrop, `View changelog`
   * and starting a walkthrough. Marks **only what was shown** (§2.2), minus the
   * two kinds of entry §2.6 keeps insisting with; `entriesToMarkOnExit` holds
   * that rule so this and `complete` cannot disagree about it.
   */
  const dismiss = () => {
    markSeen(entriesToMarkOnExit(shownEntries, "dismiss", Date.now()));
    onClose();
  };

  /**
   * `Done` on the last card — the whole recorrido counts as read (§2.2).
   *
   * The recorrido, not the release: a month past the cap leaves its overflow
   * unmarked, so the card stays tinted and the closing card's link to the
   * archive is the honest way to clear it. Marking entries the dialog never
   * showed is the exact failure CTX-591 removed.
   */
  const complete = () => {
    markSeen(entriesToMarkOnExit(walked, "complete", Date.now()));
    onClose();
  };

  /**
   * The archive, landing on the entry the reader is looking at. `EntryCard`
   * renders `id={entryKey}` and the page scrolls to and highlights
   * `location.hash`, so the hash is a working target — which is why this ships
   * without a per-entry link field on the entry contract.
   *
   * This is the modal's only link to the archive (CTX-532). The body used to
   * carry a second one 40px away, and the pair read as a duplicate even though
   * they went to different places; collapsing them here is what removes the
   * duplication without losing the deep link CTX-498 fixed.
   */
  const goToChangelog = () => {
    dismiss();
    // The closing card stands for the overflow rather than for one change, so
    // it lands at the top of the feed — there is no single entry to deep-link.
    navigate(entry ? `/changelog#${entry.entryKey}` : "/changelog");
  };

  /**
   * The CTA exists only when the entry says where the change landed **and**
   * this tenant can get there (CTX-516). `canReachPath` composes the rail's own
   * rules, so the button and the menu cannot disagree about what the tenant
   * has.
   *
   * Both misses render nothing at all (CTX-532) — no walkthrough, and a
   * walkthrough this tenant cannot reach. There is nowhere else for the slot to
   * send them that the footer does not already cover.
   */
  const walkthrough = entry?.walkthrough ?? null;
  const canWalkThrough =
    walkthrough !== null &&
    canReachPath(walkthrough.path, {
      productList: ProductList,
      tenantType: TenantType,
      permissions: ListPermiso,
      alertsSelection: { tenantId, partnerId, tenantName, partnerName },
    });

  const startWalkthrough = () => {
    if (!walkthrough || !entry) return;
    const { entryKey } = entry;
    // Close first: the tour runs on the destination, and a dialog still open
    // over it would be spotlighting its own backdrop. `dismiss` leaves this
    // entry unmarked on purpose (§2.6) — taking the tour is what reads it, so
    // the tour itself reports back.
    dismiss();
    launchWalkthrough(walkthrough, {
      onCompleted: () => markSeen([entryKey]),
    });
  };

  const title = (
    <span className="flex w-full items-center gap-3 pr-8">
      <span>What&apos;s new</span>
      <StatusBadge
        variant="info"
        withIcon={false}
        label={formatReleasePeriod(group.date)}
      />
      {/* The date belongs to the entry on screen, so a month spanning several
          deploys stays honest — and the closing card, which belongs to no
          single entry, shows none rather than borrowing one. */}
      {entry && (
        <span className="ml-auto text-xs font-normal text-semantic-text-secondary">
          {formatReleaseDate(entry.releasedAt)}
        </span>
      )}
    </span>
  );

  const footer = (
    <div className="flex w-full items-center gap-3">
      {isPaginated && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1" aria-hidden>
            {cardKeys.map((cardKey, itemIndex) => (
              <span
                key={cardKey}
                className={`h-1.5 rounded-full transition-all duration-200 motion-reduce:transition-none ${
                  itemIndex === position
                    ? "w-4 bg-semantic-bg-brand-bold"
                    : "w-1.5 bg-semantic-border-primary"
                }`}
              />
            ))}
          </span>
          <span className="text-xs text-semantic-text-secondary">
            {position + 1} of {total}
          </span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={goToChangelog}
          className="rounded text-xs font-medium text-semantic-text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand-secondary"
        >
          View changelog
        </button>

        {isPaginated && (
          <>
            <button
              type="button"
              onClick={() => showEntryAt(position - 1)}
              disabled={position === 0}
              aria-label="Previous update"
              className={ARROW_CLASS}
            >
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => showEntryAt(position + 1)}
              disabled={isLast}
              aria-label="Next update"
              className={ARROW_CLASS}
            >
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m8.25 4.5 7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          </>
        )}

        {/* On the last entry the primary action closes instead of advancing —
            a `Next` that does nothing is the one affordance a walkthrough must
            not end on. */}
        <GenericButton
          variant="brand"
          size="sm"
          label={isLast ? "Done" : "Next"}
          onClick={isLast ? complete : () => showEntryAt(position + 1)}
        />
      </div>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={dismiss}
      title={title}
      footer={footer}
      customWidth="max-w-2xl"
    >
      <div className="flex flex-col gap-4">
        {entry ? (
          <>
            <WhatsNewMedia entry={entry} />

            <div>
              <h3 className="text-base font-semibold text-semantic-text-primary">
                {entry.title}
              </h3>
              <p className="mt-1.5 text-sm text-semantic-text-secondary">
                {entry.description}
              </p>
            </div>

            {entry.actionRequired && (
              <GenericInlineAlert variant="warning" title="Action required">
                {entry.actionRequired}
              </GenericInlineAlert>
            )}

            {/* The body's one action, or nothing. An entry with nowhere to walk
                to leaves the slot empty rather than repeating the footer's link
                back at the reader (CTX-532). */}
            {canWalkThrough && (
              <GenericButton
                variant="outlined"
                size="sm"
                className="self-start"
                icon={<CursorRaysIcon />}
                iconPosition="left"
                label="Walk me through it"
                onClick={startWalkthrough}
              />
            )}
          </>
        ) : (
          /* The closing card of a month past the cap (§2.5). It carries a count
             and no action: the footer's `View changelog` is already the link,
             and on this card it lands at the top of the feed rather than on an
             entry — a second button 40px away is the duplication CTX-532
             removed from the body in the first place. */
          <div className="py-2">
            <h3 className="text-base font-semibold text-semantic-text-primary">
              {overflowCount} more {overflowCount === 1 ? "change" : "changes"} in
              the changelog
            </h3>
            <p className="mt-1.5 text-sm text-semantic-text-secondary">
              This walkthrough covers the {walked.length} most recent changes of{" "}
              {formatReleasePeriod(group.date)}. The rest are in the changelog,
              along with everything that came before them.
            </p>
          </div>
        )}
      </div>
    </GenericModal>
  );
};

export default WhatsNewModal;
